import React, { useState, useEffect, useRef } from "react";
import {
  fetchMembershipTypes,
  createMembershipType,
  updateMembershipType,
  deleteMembershipType,
} from "../../api/adminMasterApi";
import { getCsrfToken } from "../../../../public/api/authApi";
import {
  GlobalButton,
  GlobalTextField,
  GlobalSelectField,
} from "../../../../global/forms/GlobalFormFields";
import { GlobalTableActionButton } from "../../../../global/GlobalTableActionButton";
import GlobalFormShell from "../../../../global/forms/GlobalFormShell";
import { GlobalModal } from "../../../../global/GlobalModal";
import GlobalDataTable from "../../../../global/GlobalDataTable";
import GlobalBadge from "../../../../global/GlobalBadge";
import GlobalSuccessFlashModal from "../../../../global/GlobalSuccessFlashModal";

const STATUS_OPTIONS = [
  { value: 1, label: "Active" },
  { value: 2, label: "Inactive" },
];

const MembershipTypeMaster = ({
  onDirectoryChange,
  canCreate = false,
  canEdit = false,
  canDelete = false,
}) => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    id: null,
    type_name: "",
    description: "",
    status_id: 1,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formErrorDetails, setFormErrorDetails] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const alertRef = useRef(null);
  const formSectionRef = useRef(null);
  const [successMsg, setSuccessMsg] = useState("");

  const scrollToMasterForm = () => {
    requestAnimationFrame(() => {
      formSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    });
  };

  useEffect(() => {
    loadTypes();
  }, []);

  const loadTypes = async () => {
    try {
      setLoading(true);
      const response = await fetchMembershipTypes();
      if (response && response.success) {
        setTypes(response.data || []);
      } else {
        setTypes(response || []);
      }
    } catch (err) {
      setError(err.message || "Failed to load membership types");
    } finally {
      setLoading(false);
    }
  };

  const setFormField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrorDetails[name]) {
      setFormErrorDetails((prev) => ({ ...prev, [name]: "" }));
    }
    setFormError(null);
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (isEditing ? !canEdit : !canCreate) return;
    setFormError(null);
    setFormErrorDetails({});
    setSuccessMsg("");
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }

      let result;
      if (isEditing) {
        result = await updateMembershipType(
          formData.id,
          {
            type_name: formData.type_name.trim(),
            description: formData.description,
            status_id: Number(formData.status_id),
          },
          csrfToken,
        );
      } else {
        result = await createMembershipType(
          {
            type_name: formData.type_name,
            description: formData.description,
          },
          csrfToken,
        );
      }
      if (result?.error) {
        setFormError(result.error.message || "Failed to save membership type");
        setFormErrorDetails(result.error.details || {});
        setTimeout(() => alertRef.current?.focus(), 0);
        return;
      }
      if (result?.reactivated) {
        setSuccessMsg("Membership type restored to the directory.");
      } else {
        setSuccessMsg(
          isEditing
            ? "Membership type updated successfully."
            : "Membership type created successfully.",
        );
      }
      handleCancel();
      await loadTypes();
      onDirectoryChange?.();
    } catch (err) {
      setFormError(err.message);
      setTimeout(() => alertRef.current?.focus(), 0);
    }
  };

  const handleEdit = (type) => {
    setSuccessMsg("");
    setFormData({
      id: type.id,
      type_name: type.type_name,
      description: type.description || "",
      status_id:
        type.status_id != null && type.status_id !== ""
          ? Number(type.status_id)
          : 1,
    });
    setIsEditing(true);
    scrollToMasterForm();
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }
      await deleteMembershipType(itemToDelete.id, csrfToken);
      setShowDeleteConfirm(false);
      setItemToDelete(null);
      setSuccessMsg("Membership type removed from the directory.");
      await loadTypes();
      onDirectoryChange?.();
    } catch (err) {
      alert("Failed to remove: " + err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    setFormData({ id: null, type_name: "", description: "", status_id: 1 });
    setFormError(null);
    setFormErrorDetails({});
    setIsEditing(false);
  };

  if (loading) return <p className="ub-loading">Loading Membership Types…</p>;
  if (error) return <p className="ub-error">Error: {error}</p>;

  const dataList = Array.isArray(types) ? types : types.data || [];
  const ALERT_ID = "ms-type-master-alert";

  return (
    <div className="ub-master-section">
      <GlobalSuccessFlashModal
        message={successMsg}
        onDismiss={() => setSuccessMsg("")}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1.25rem",
        }}
      >
        <h3 className="ub-section__subhead" style={{ margin: 0 }}>
          Membership Type Master
        </h3>
        {/* <GlobalButton
          variant="primary"
          size="sm"
          onClick={() => {
            setSuccessMsg("");
            setFormData({
              id: null,
              type_name: "",
              description: "",
              status_id: 1,
            });
            setIsEditing(false);
            setFormError(null);
            setFormErrorDetails({});
            scrollToMasterForm();
          }}
        >
          Add New Type
        </GlobalButton> */}
      </div>

      <div ref={formSectionRef} style={{ scrollMarginTop: "1rem" }}>
        {canCreate || canEdit ? (
        <GlobalFormShell
          legend={isEditing ? "Edit membership type" : "Add membership type"}
          showAlert={!!formError}
          alertText={formError}
          alertId={ALERT_ID}
          formAlertRef={alertRef}
          actions={
            <>
              {isEditing ? (
                <GlobalButton variant="ghost" onClick={handleCancel}>
                  Cancel edit
                </GlobalButton>
              ) : null}
              <GlobalButton
                variant="primary"
                onClick={handleSubmit}
                disabled={isEditing ? !canEdit : !canCreate}
              >
                {isEditing ? "Save type" : "Create type"}
              </GlobalButton>
            </>
          }
        >
          <GlobalTextField
            id={isEditing ? "type_name_edit" : "type_name"}
            label="Type Name"
            value={formData.type_name}
            onChange={(v) => setFormField("type_name", v)}
            error={formErrorDetails.type_name}
            baseAlertId={ALERT_ID}
            required
          />
          <GlobalTextField
            id={isEditing ? "description_edit" : "description"}
            label="Description"
            value={formData.description}
            onChange={(v) => setFormField("description", v)}
            error={formErrorDetails.description}
            baseAlertId={ALERT_ID}
          />
          {isEditing ? (
            <GlobalSelectField
              id="membership_type_status_id"
              label="Status"
              value={String(formData.status_id)}
              onChange={(v) => setFormField("status_id", Number(v))}
              options={STATUS_OPTIONS}
              error={formErrorDetails.status_id}
              baseAlertId={ALERT_ID}
            />
          ) : null}
        </GlobalFormShell>
        ) : null}
      </div>

      <GlobalDataTable
        wrapClassName="ub-table-wrap"
        tableClassName="ub-table"
        tableAriaLabel="Membership type master table"
        headers={[
          { key: "name", label: "Type Name" },
          { key: "desc", label: "Description" },
          { key: "status", label: "Status" },
          { key: "created", label: "Created Date" },
          {
            key: "actions",
            label: "Actions",
            className: "ub-table__col-actions",
          },
        ]}
      >
        {dataList.map((type) => (
          <tr key={type.id}>
            <td style={{ fontWeight: "500" }}>{type.type_name}</td>
            <td>{type.description || "—"}</td>
            <td>
              <GlobalBadge active={Number(type.status_id) === 1} />
            </td>
            <td>
              {type.created_at
                ? new Date(type.created_at).toLocaleDateString()
                : "—"}
            </td>
            <td className="ub-table__col-actions">
              <div className="ub-table-actions">
                {canEdit ? (
                  <GlobalTableActionButton action="edit" onClick={() => handleEdit(type)} />
                ) : null}
                {canDelete ? (
                  <GlobalTableActionButton action="delete" onClick={() => handleDeleteClick(type)} />
                ) : null}
              </div>
            </td>
          </tr>
        ))}
        {dataList.length === 0 && (
          <tr>
            <td
              colSpan={5}
              style={{ textAlign: "center", padding: "2rem", color: "#6b7280" }}
            >
              No Membership Types Found
            </td>
          </tr>
        )}
      </GlobalDataTable>

      <GlobalModal
        open={showDeleteConfirm}
        onClose={() => !isDeleting && setShowDeleteConfirm(false)}
        title="Confirm removal"
        footer={
          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              justifyContent: "flex-end",
            }}
          >
            <GlobalButton
              onClick={() => setShowDeleteConfirm(false)}
              disabled={isDeleting}
            >
              Cancel
            </GlobalButton>
            <GlobalButton
              variant="danger"
              onClick={handleConfirmDelete}
              loading={isDeleting}
            >
              Remove
            </GlobalButton>
          </div>
        }
      >
        <p>
          Remove membership type <b>{itemToDelete?.type_name}</b> from the
          directory? It will be hidden from lists (marked inactive).
        </p>
      </GlobalModal>
    </div>
  );
};

export default MembershipTypeMaster;
